/**
 * (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import {
  DataConnectionStep,
  DataConnectionType,
  isDataConnectionDialogOpen,
  useDataConnectionActions,
} from "../data-connection-flow";
import { DataConnectionState } from "../data-connection-flow/data-connection-types";
import { bluetoothUniversalHex } from "../device/get-hex-file";
import { isAndroid } from "../platform";
import { useStore } from "../store";
import BluetoothConnectingDialog from "./BluetoothConnectingDialog";
import PermissionErrorDialog from "./BluetoothPermissionErrorDialog";
import BrokenFirmwareDialog from "./BrokenFirmwareDialog";
import ConnectBatteryDialog from "./ConnectBatteryDialog";
import ConnectCableDialog, {
  getConnectionCableDialogConfig,
} from "./ConnectCableDialog";
import ConnectErrorDialog, {
  ConnectionErrorDeviceType,
} from "./ConnectErrorDialog";
import DownloadProgressDialog, {
  getHeadingId as getDownloadProgressHeadingId,
} from "./DownloadProgressDialog";
import EnterBluetoothPatternDialog from "./EnterBluetoothPatternDialog";
import LoadingDialog from "./LoadingDialog";
import ManualFlashingDialog from "./ManualFlashingDialog";
import NativeBluetoothConnectBatteryDialog from "./NativeBluetoothConnectBatteryDialog";
import NativeBluetoothErrorDialog from "./NativeBluetoothErrorDialog";
import NativeBluetoothPairingLostDialog from "./NativeBluetoothPairingLostDialog";
import ResetToBluetoothModeDialog from "./ResetToBluetoothModeDialog";
import ResetToBluetoothModeTroubleshootDialog from "./ResetToBluetoothModeTroubleshootDialog";
import SelectMicrobitBluetoothDialog from "./SelectMicrobitBluetoothDialog";
import SelectMicrobitUsbDialog, {
  getHeadingId as getSelectMicrobitUsbHeadingId,
} from "./SelectMicrobitUsbDialog";
import TryAgainDialog from "./TryAgainDialog";
import UnsupportedMicrobitDialog from "./UnsupportedMicrobitDialog";
import WebUsbBluetoothUnsupportedDialog from "./WebUsbBluetoothUnsupportedDialog";
import WhatYouWillNeedDialog from "./WhatYouWillNeedDialog";

const getDeviceType = (state: DataConnectionState): ConnectionErrorDeviceType =>
  state.type === DataConnectionType.Radio
    ? state.lastDisconnectSource === "bridge"
      ? "bridge"
      : "remote"
    : "bluetooth";

const DataConnectionDialogs = () => {
  const state = useStore((s) => s.dataConnection);
  const actions = useDataConnectionActions();
  const flashProgress = useStore((s) => s.dataConnectionFlashingProgress);
  const onClose = actions.close;

  const isOpen = isDataConnectionDialogOpen(state.step);

  const dialogCommonProps = { isOpen, onClose };

  switch (state.step) {
    case DataConnectionStep.StartOver:
    case DataConnectionStep.Start: {
      if (state.type === DataConnectionType.NativeBluetooth) {
        return (
          <NativeBluetoothConnectBatteryDialog
            {...dialogCommonProps}
            onNextClick={actions.onNextClick}
            reconnect={state.step === DataConnectionStep.StartOver}
          />
        );
      }
      return (
        <WhatYouWillNeedDialog
          type={state.type === DataConnectionType.Radio ? "radio" : "bluetooth"}
          {...dialogCommonProps}
          onLinkClick={
            actions.canSwitchFlowType() ? actions.switchFlowType : undefined
          }
          onNextClick={actions.onNextClick}
          reconnect={state.step === DataConnectionStep.StartOver}
        />
      );
    }
    case DataConnectionStep.NativeBluetoothPreConnectTutorial: {
      return (
        <ResetToBluetoothModeDialog
          {...dialogCommonProps}
          onBackClick={actions.onBackClick}
          onNextClick={actions.onNextClick}
          onTroubleshooting={actions.troubleshootPairingMethod}
        />
      );
    }
    case DataConnectionStep.NativeBluetoothPreConnectTroubleshooting: {
      return (
        <ResetToBluetoothModeTroubleshootDialog
          {...dialogCommonProps}
          onTryAgain={actions.onTryAgain}
        />
      );
    }
    case DataConnectionStep.ConnectCable: {
      const config = getConnectionCableDialogConfig(
        state.type,
        actions.canSwitchFlowType(),
        state.radioFlowPhase
      );
      return (
        <ConnectCableDialog
          {...dialogCommonProps}
          onBackClick={actions.onBackClick}
          onNextClick={actions.onNextClick}
          config={config}
          onSkip={actions.onSkip}
          onSwitch={actions.switchFlowType}
        />
      );
    }
    case DataConnectionStep.WebUsbFlashingTutorial: {
      return (
        <SelectMicrobitUsbDialog
          headingId={getSelectMicrobitUsbHeadingId(
            state.type,
            state.radioFlowPhase
          )}
          {...dialogCommonProps}
          onBackClick={actions.onBackClick}
          onNextClick={actions.onNextClick}
        />
      );
    }
    // Only bluetooth mode has this fallback, the radio bridge mode requires working WebUSB.
    case DataConnectionStep.ManualFlashingTutorial: {
      return (
        <ManualFlashingDialog
          {...dialogCommonProps}
          hex={bluetoothUniversalHex}
          onNextClick={actions.onNextClick}
          onBackClick={actions.onBackClick}
        />
      );
    }
    case DataConnectionStep.ConnectBattery: {
      return (
        <ConnectBatteryDialog
          {...dialogCommonProps}
          onBackClick={actions.onBackClick}
          onNextClick={actions.onNextClick}
        />
      );
    }
    case DataConnectionStep.EnterBluetoothPattern: {
      return (
        <EnterBluetoothPatternDialog
          {...dialogCommonProps}
          onBackClick={actions.onBackClick}
          onNextClick={actions.onNextClick}
          microbitName={state.bluetoothMicrobitName}
          onChangeMicrobitName={actions.onChangeMicrobitName}
        />
      );
    }
    case DataConnectionStep.WebBluetoothPreConnectTutorial: {
      return (
        <SelectMicrobitBluetoothDialog
          {...dialogCommonProps}
          onBackClick={actions.onBackClick}
          onNextClick={actions.onNextClick}
        />
      );
    }
    case DataConnectionStep.FlashingInProgress: {
      return (
        <DownloadProgressDialog
          headingId={getDownloadProgressHeadingId(
            state.type,
            state.radioFlowPhase
          )}
          isOpen={isOpen}
          stage={flashProgress.stage}
          progress={flashProgress.value}
          tryAgain={actions.onTryAgain}
          microbitName={state.bluetoothMicrobitName}
        />
      );
    }
    case DataConnectionStep.BluetoothConnect: {
      return (
        <BluetoothConnectingDialog
          isOpen={isOpen}
          stage={flashProgress.stage}
        />
      );
    }
    case DataConnectionStep.ConnectingMicrobits: {
      return (
        <LoadingDialog isOpen={isOpen} headingId="connect-radio-heading" />
      );
    }
    case DataConnectionStep.TryAgainBluetoothSelectMicrobit:
    case DataConnectionStep.TryAgainReplugMicrobit:
    case DataConnectionStep.TryAgainWebUsbSelectMicrobit:
    case DataConnectionStep.TryAgainCloseTabs: {
      return (
        <TryAgainDialog
          {...dialogCommonProps}
          onTryAgain={actions.onTryAgain}
          type={state.step}
        />
      );
    }
    case DataConnectionStep.BadFirmware: {
      return (
        <BrokenFirmwareDialog
          {...dialogCommonProps}
          onTryAgain={actions.onTryAgain}
        />
      );
    }
    case DataConnectionStep.MicrobitUnsupported: {
      return (
        <UnsupportedMicrobitDialog
          {...dialogCommonProps}
          onStartBluetoothClick={actions.onStartBluetoothFlow}
          isBluetoothSupported={state.isWebBluetoothSupported}
        />
      );
    }
    case DataConnectionStep.WebUsbBluetoothUnsupported: {
      return <WebUsbBluetoothUnsupportedDialog {...dialogCommonProps} />;
    }
    case DataConnectionStep.ConnectFailed: {
      // Native Bluetooth: show native-specific error dialog with pattern/mode/distance advice
      if (state.type === DataConnectionType.NativeBluetooth) {
        return (
          <NativeBluetoothErrorDialog
            {...dialogCommonProps}
            onTryAgain={actions.onNextClick}
          />
        );
      }
      // WebBluetooth/Radio: show standard connect error dialog
      return (
        <ConnectErrorDialog
          {...dialogCommonProps}
          onRetry={actions.onNextClick}
          variant={
            state.hadSuccessfulConnection ? "reconnectFailed" : "connectFailed"
          }
          deviceType={getDeviceType(state)}
        />
      );
    }
    case DataConnectionStep.PairingLost: {
      return (
        <NativeBluetoothPairingLostDialog
          {...dialogCommonProps}
          onTryAgain={actions.onNextClick}
        />
      );
    }
    case DataConnectionStep.ConnectionLost: {
      // Connection was working then lost - standard dialog is appropriate for all flows
      return (
        <ConnectErrorDialog
          {...dialogCommonProps}
          onRetry={actions.onNextClick}
          variant="connectionLost"
          deviceType={getDeviceType(state)}
        />
      );
    }
    case DataConnectionStep.BluetoothDisabled:
      return (
        <PermissionErrorDialog
          headingId="bluetooth-disabled-heading"
          bodyId="bluetooth-disabled-body"
          {...dialogCommonProps}
          onTryAgain={actions.onTryAgain}
          isCheckingPermissions={state.isCheckingPermissions}
        />
      );
    case DataConnectionStep.BluetoothPermissionDenied:
      return (
        <PermissionErrorDialog
          headingId="bluetooth-permission-denied-heading"
          bodyId={
            isAndroid()
              ? "bluetooth-permission-denied-body-android"
              : "bluetooth-permission-denied-body-ios"
          }
          {...dialogCommonProps}
          onTryAgain={actions.onTryAgain}
          onOpenSettings={actions.openAppSettings}
          isCheckingPermissions={state.isCheckingPermissions}
        />
      );
    case DataConnectionStep.LocationDisabled:
      return (
        <PermissionErrorDialog
          headingId="location-disabled-heading"
          bodyId="location-disabled-body"
          {...dialogCommonProps}
          onTryAgain={actions.onTryAgain}
          onOpenSettings={actions.openLocationSettings}
          isCheckingPermissions={state.isCheckingPermissions}
        />
      );
  }
};

export default DataConnectionDialogs;
